import dayjs from 'dayjs'
import _ from 'lodash'
import { useEffect, useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { getMiningRewards, getRoundTradeMiningReport } from '~/apis'
import type { MiningRewards } from '~/apis'
import Tooltip from '~/components/Tooltip'
import Clock from '~/components/Clock'
import { LOCALES } from '~/locales'
import { formatNumber, numberToOrdinalWithSuffix } from '~/utils/numbers'
import {
  StyledCardsWrapper,
  StyledTimerWrapper,
  StyledReportWrapper,
  StyledReportIcon,
  StyledReportHeader,
  StyledReportContent,
  StyledReportItem,
  StyledReportCalcIcon,
  StyledReportCalcItem,
} from './styled'
import IconMoney from '~/assets/images/money-round-green.svg'
import IconContact from '~/assets/images/contact-round-green.svg'
import IconTrophy from '~/assets/images/trophy-round-green.svg'
import IconTime from '~/assets/images/time.svg'

const convertNumberTolocale = (num: number, locale: string) => {
  if (typeof num !== 'number') {
    return '~'
  }
  if (locale === 'zh-cn') {
    return `${num}`
  }
  return numberToOrdinalWithSuffix(num)
}

const getPhasePeriod = (miningRecords: MiningRewards, phase: number) => {
  let phasePeriod = '~'
  const targetPool = miningRecords?.pools?.find((pool) => pool.phase === phase)
  if (targetPool) {
    phasePeriod = `${dayjs(targetPool.startedAt * 1000).format(
      'YYYY-MM-DD',
    )} ~ ${dayjs(targetPool?.closedAt * 1000).format('YYYY-MM-DD')}`
  }
  return phasePeriod
}

const MiningData: React.FC = () => {
  const { i18n, t } = useTranslation()
  const isZh = i18n.resolvedLanguage === LOCALES.ZH
  const lang = isZh ? 'zh-cn' : 'en-us'
  const weeklyReportUrl = `https://support.tokenlon.im/hc/${lang}/sections/360012812292`

  const [currentPhase, setCurrentPhase] = useState<number>()
  const [lastPhase, setLastPhase] = useState<number>()
  const [countdownTargetDate, setCountdownTargetDate] = useState<number>()

  const { data: miningRecords } = useSWR('getMiningRewards', getMiningRewards)
  const { data: lastRoundReport } = useSWR(
    lastPhase ? ['getRoundTradeMiningReport', lastPhase] : null,
    () => getRoundTradeMiningReport(lastPhase),
  )
  const { lon, volume_in_usd, wallets } = lastRoundReport ?? {}
  const isReportCalculating = !lon || !volume_in_usd || !wallets

  const currentPhasePeriod = useMemo(() => {
    return getPhasePeriod(miningRecords, currentPhase)
  }, [miningRecords, currentPhase])

  const lastPhasePeriod = useMemo(() => {
    return getPhasePeriod(miningRecords, lastPhase)
  }, [miningRecords, lastPhase])

  const currentPhaseLiteral = convertNumberTolocale(currentPhase, lang)
  const lastPhaseLiteral = convertNumberTolocale(lastPhase, lang)

  useEffect(() => {
    if (!!miningRecords) {
      const currentPhase = miningRecords.currentPhase
      setCurrentPhase(currentPhase)

      const currentPool = miningRecords.pools?.find(
        (pool) => pool.phase === currentPhase,
      )
      if (currentPool) {
        setCountdownTargetDate(currentPool.closedAt * 1000)
        setLastPhase(currentPhase - 1)
      }
    }
  }, [miningRecords])

  return (
    <section className="mining-data">
      <div className="title">
        <p>{t('mining_data')}</p>
      </div>
      <StyledCardsWrapper>
        <StyledTimerWrapper className="card">
          <span className="countdown-title">
            {t('current_trade_mining_countdown_title', {
              phase: currentPhaseLiteral,
            })}
            <Tooltip
              className="icon-exclamation"
              placement="topLeft"
              arrowPointAtCenter
              title={t('current_trade_mining_countdown_tip')}
            />
          </span>
          <Clock targetDate={countdownTargetDate} />
          <span className="currentPeriod">{currentPhasePeriod}</span>
        </StyledTimerWrapper>

        <StyledReportWrapper className="card">
          <StyledReportHeader>
            <span>{t('trade_mining_report', { phase: lastPhaseLiteral })}</span>
            <a href={weeklyReportUrl} target="_blank">
              {`${t('more_details')} >`}
            </a>
          </StyledReportHeader>
          <StyledReportContent>
            <StyledReportItem>
              <StyledReportIcon src={IconMoney} />
              <span className="label">{t('trade_mining_total_volume')}</span>
              <span className="report-divider"></span>
              {!isReportCalculating && (
                <span className="value">{`$${formatNumber(
                  lastRoundReport?.volume_in_usd,
                  2,
                )}`}</span>
              )}
            </StyledReportItem>
            <StyledReportItem>
              <StyledReportIcon src={IconTrophy} />
              <span className="label">{t('trade_mining_total_rewards')}</span>
              <span className="report-divider"></span>
              {!isReportCalculating ? (
                <span className="value">{`${formatNumber(
                  lastRoundReport?.lon,
                  2,
                )} LON`}</span>
              ) : (
                <StyledReportCalcItem>
                  <StyledReportCalcIcon src={IconTime} />
                  <span>{t('calculating')}</span>
                </StyledReportCalcItem>
              )}
            </StyledReportItem>
            <StyledReportItem>
              <StyledReportIcon src={IconContact} />
              <span className="label">{t('trade_mining_participants')}</span>
              <span className="report-divider"></span>
              {!isReportCalculating && (
                <span className="value">{`${formatNumber(
                  lastRoundReport?.wallets,
                )}`}</span>
              )}
            </StyledReportItem>
          </StyledReportContent>
          <span className="lastPeriod">{lastPhasePeriod}</span>
        </StyledReportWrapper>
      </StyledCardsWrapper>
    </section>
  )
}

export default MiningData
